import { Topbar } from '../../components/Topbar/Topbar';
import { Alert, Badge, Button, Card, Container, Form, ListGroup } from 'react-bootstrap';
import { Footer } from '../../components/Footer/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartPlus, faHistory, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { useStripe } from '@stripe/react-stripe-js';
import { Browser } from '@capacitor/browser';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { Link, Redirect } from 'react-router-dom';
import { formatSecondsTimestamp } from '../../utils/utils';
import { Trans, useTranslation } from 'react-i18next';
import './Gift.scss';
import { Title } from '../../components/Title/Title';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';

const GiftComponent = () => {
  const { getStripeOneOffs, createOneOffSession, userDataFmt, staticData } = useFirebase();
  const { t } = useTranslation();
  const { currentLanguage } = useCurrentLanguage();

  const { isNative } = useCapacitor();
  const [loading, setLoading] = useState(false);
  const [oneOffs, setOneOffs] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [cart, setCart] = useState<any[]>([]);
  const cartMap = useRef(new Map());

  const stripe = useStripe();

  useEffect(() => {
    setLoading(true);
    getStripeOneOffs()
      .then(({ products, history }) => {
        setOneOffs(products);
        setHistory(history);
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const onCartSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    createOneOffSession({ customer: userDataFmt.stripeId, cart })
      .then(async ({ sessionId, url }) => {
        if (isNative) {
          await Browser.open({ url });
        } else await stripe.redirectToCheckout({ sessionId });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const getLocalizedProperty = (item: any, prop: string) => {
    try {
      return item.metadata?.[`i18n.${prop}.${currentLanguage}`] || item[prop];
    } catch {
      return item[prop];
    }
  };

  if (loading || !userDataFmt || !stripe || !staticData || !Object.keys(staticData).length) return <FullscreenLoader />;

  if (!staticData.ENABLE_GIFTS) return <Redirect to={'/'} />;

  return (
    <>
      <Title>{t('gift.labels.regalaAbbonamento')}</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'my-5'}>
        <div className={'d-flex align-items-center'}>
          <div className={'display-4'}>{t('gift.labels.regalaAbbonamento')}</div>
        </div>

        <div className={'mt-4'}>{t('gift.labels.giftDescrizione')}</div>
        <div>{t('gift.labels.giftDescrizione2')}</div>
        {currentLanguage === 'it' && (
          <Alert variant={'info'} className={'mt-4'}>
            <h5>{t('labels.confirmInfoModalSubtitle')}</h5>
            <div>
              {t('labels.confirmInfoModalDescription1')}
              <br />
              {t('labels.confirmInfoModalDescription2')}
            </div>
          </Alert>
        )}
        {!oneOffs.length && (
          <Alert variant={'warning'} className={'mt-4 text-center'}>
            {t('gift.labels.noPacchetti')}
          </Alert>
        )}

        {!!oneOffs.length && (
          <>
            <div className={'d-flex align-items-center mt-4'}>
              <div className={'h3 mb-4'}>{t('gift.labels.aChiVuoiRegalare')}</div>
            </div>
            <Alert variant={'info'}>{t('gift.labels.scontoRegalo')}</Alert>
            <div className={'d-flex flex-column flex-md-row mt-4'}>
              {oneOffs.map((oneOff, index, arr) => (
                <Card key={oneOff.id} bg={'dark'} className={'flex-fill w-100 ' + (index == arr.length - 1 ? 'mr-0 mb-0' : 'mr-0 mr-md-3 mb-3 mb-md-0')}>
                  <Card.Body>
                    <Card.Title>{getLocalizedProperty(oneOff, 'name')}</Card.Title>
                    <div>{getLocalizedProperty(oneOff, 'description')}</div>
                    <div className={'text-right mt-3'}>
                      {oneOff.prices[0].metadata.FULL && <span className={'mr-2 deleted-price'}>&euro; {oneOff.prices[0].metadata.FULL}</span>}
                      <span className={'h4 mb-0'}>
                        <Badge variant={'success'}>€ {(oneOff.prices[0].unit_amount / 100).toFixed(2)}</Badge>
                      </span>
                    </div>
                  </Card.Body>
                  <Card.Footer className={'d-flex justify-content-end'}>
                    <Button
                      variant={'primary'}
                      size={'sm'}
                      onClick={() => {
                        if (!cartMap.current.get(oneOff.id)) cartMap.current.set(oneOff.id, 0);
                        cartMap.current.set(oneOff.id, cartMap.current.get(oneOff.id) + 1);
                        setCart((current) => [...current, { itemId: `${oneOff.id}-${cartMap.current.get(oneOff.id)}`, ...oneOff }]);
                      }}
                    >
                      <FontAwesomeIcon icon={faCartPlus} className={'mr-1'} /> {t('labels.aggiungi')}
                    </Button>
                  </Card.Footer>
                </Card>
              ))}
            </div>
            <div className={'small text-muted mt-3'}>
              {t('gift.labels.disclamer1')}
              <br />
              {t('gift.labels.disclamer2')}
              <br />
              {t('gift.labels.disclamer3')}
            </div>
          </>
        )}

        {!!oneOffs.length && (
          <>
            <div className={'d-flex align-items-center mt-4'}>
              <div className={'h3'}>
                <FontAwesomeIcon icon={faShoppingCart} className={'text-primary'} fixedWidth={true} /> {t('labels.carrello')}
              </div>
            </div>

            {!cart.length && (
              <>
                <div className={'text-center'} style={{ fontSize: '1.2rem' }}>
                  {t('gift.labels.carrelloVuoto')}
                </div>
                <div className={'text-center text-muted'}>{t('gift.labels.aggiungiSconti')}</div>
              </>
            )}

            {!!cart.length && (
              <Form onSubmit={onCartSubmit}>
                <div>{t('gift.labels.compilaEmail')}</div>
                <div>{t('gift.labels.compilaEmailDescrizione')}</div>
                <ListGroup className={'mt-2'}>
                  {cart.map((item) => (
                    <ListGroup.Item key={item.itemId} variant={'dark'} className={'dark-list-item'}>
                      <div className={'d-flex flex-column flex-md-row align-items-start align-items-md-center'}>
                        <div className={'d-flex flex-column flex-grow-1'}>
                          <div>
                            <h5 className={'mb-0'}>{getLocalizedProperty(item, 'name')}</h5>
                          </div>
                          <div>€ {(item.prices[0].unit_amount / 100).toFixed(2)}</div>
                        </div>
                        <div className={'d-flex'}>
                          <Form.Group className={'mb-0 mr-2'}>
                            <Form.Control
                              placeholder={t('gift.labels.nomeDestinatario')}
                              required
                              value={item.recipientName}
                              onChange={(e) => (item.recipientName = e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className={'mb-0'}>
                            <Form.Control
                              placeholder={t('gift.labels.emailDestinatario')}
                              type={'email'}
                              required
                              value={item.recipient}
                              onChange={(e) => (item.recipient = e.target.value)}
                            />
                          </Form.Group>
                        </div>
                        <div className={'ml-2'}>
                          <Button
                            variant={'outline-danger'}
                            onClick={() => {
                              setCart((current) => current.filter((i) => i.itemId != item.itemId));
                              cartMap.current.set(item.id, cartMap.current.get(item.id) - 1);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                  <ListGroup.Item variant={'primary'} className={'dark-list-item'}>
                    <div className={'d-flex justify-content-between'}>
                      <h5 className={'mb-0'}>Totale:</h5>
                      <strong>€ {(cart.map((item) => item.prices[0].unit_amount).reduce((acc, val) => acc + val) / 100).toFixed(2)}</strong>
                    </div>
                  </ListGroup.Item>
                </ListGroup>
                <div className={'d-flex justify-content-end mt-2'}>
                  <Button variant={'primary'} type={'submit'}>
                    {t('gift.labels.procediPagamento')}
                  </Button>
                </div>
              </Form>
            )}
          </>
        )}

        <div className={'d-flex align-items-center mt-4'}>
          <div className={'h3'}>
            <FontAwesomeIcon icon={faHistory} className={'text-primary'} fixedWidth={true} /> {t('labels.storico')}
          </div>
        </div>
        {!history.length && (
          <>
            <div className={'text-center'} style={{ fontSize: '1.2rem' }}>
              {t('gift.labels.nessunAcquisto')}
            </div>
            <div className={'text-center text-muted'}>{t('gift.labels.nessunAcquistoDescrizione')}</div>
          </>
        )}

        {!!history.length && (
          <ListGroup>
            {history.map((item) => (
              <ListGroup.Item variant={'dark'} className={'dark-list-item'} key={item.id}>
                <div className={'d-flex justify-content-between align-items-center'}>
                  <div className={'flex-fill'}>
                    {t('gift.labels.codici', { date: formatSecondsTimestamp(item.ts._seconds, 'dd/MM/y HH:mm'), length: item.coupons.length })}
                  </div>
                  <div>
                    <Button variant={'primary'} size={'sm'} as={Link} to={`/gift/details/${item.id}`}>
                      {t('labels.dettagli')}
                    </Button>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Container>

      <Footer />
    </>
  );
};

export default GiftComponent;
